import { useMemo } from "react";
import usePipeline from "@/features/stage/api/usePipeline";
import { impliedStageIds } from "@/features/stage/utils/impliedPipelineSelection";
import { useFavoriteViews } from "../api/useFavoriteViews";
import { parseViewRouteId } from "../types";
import type { FavoriteViewPayload } from "../types";

const csv = (raw: unknown): string[] =>
  String(raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * The stage a saved view is pinned to — explicitly, or as its own filters
 * already define it.
 *
 * LEGACY VIEWS. `FavoriteViewPayload.stageId` only exists for views saved
 * after 2026-09-23; every view made before that lost its stage on save (a
 * stage route pins the stage by ROUTE, so it was never one of the chips).
 * Those views can't be backfilled from the URL — the path suffix is
 * `-<tenantId>-<viewId>` — but the filters usually say it anyway: a status
 * belongs to exactly one stage, so "Status: Offer Letter Issued" IS
 * /Offer Letter (COL). Same fact the pickers now display
 * (`impliedPipelineSelection`); this just lets the LIST act on it, so a view
 * made before the fix still opens as the stage route it was made on.
 *
 * TWO GUARDS, both about never silently re-meaning a saved view:
 *
 *  - Exactly ONE stage. Two stages is a cross-stage view, which a stage route
 *    cannot be; ambiguity means leave it alone.
 *  - No saved DATE RANGE. Pinning hands the window to the stage clock, so
 *    "created in September" would quietly become "moved into this stage in
 *    September" — a different number under the same view name. A view that
 *    carries a range keeps exactly the behaviour it has today; the inference
 *    only ever changes ORDER and deal scoping on views that have no window to
 *    re-mean.
 *
 * An explicit `stageId` skips both: the user saved it FROM that stage.
 */
export function inferViewStageId(
  payload: FavoriteViewPayload | null | undefined,
  pipeline: unknown,
): number | null {
  const explicit = Number(payload?.stageId);
  if (Number.isInteger(explicit) && explicit > 0) return explicit;

  const filters = (payload?.filters ?? {}) as Record<string, any>;
  if (String(filters.startDate ?? "").trim()) return null;

  const stageChips = csv(filters.stage);
  const candidates =
    stageChips.length > 0
      ? stageChips
      : impliedStageIds(
          pipeline as any,
          csv(filters.status),
          csv(filters.subStatus),
        );
  if (candidates.length !== 1) return null;

  const id = Number(candidates[0]);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Hook form of {@link inferViewStageId} for a route. `null` off a view route,
 * for a stage-less view, and while the view list is loading.
 *
 * Why the stage is not a filter field: `applyDynamicFilters` rebuilds
 * `appliedFilters` from the known `FilterState` keys, so parking `stageId`
 * there would drop it the first time the user pressed Apply and the view would
 * quietly stop being that stage's list.
 */
export function useViewPinnedStageId(
  routeId: string | number | undefined,
): number | null {
  const viewId = parseViewRouteId(routeId);
  const { views } = useFavoriteViews({ enabled: viewId != null });
  const { pipeline } = usePipeline();
  return useMemo(() => {
    if (viewId == null) return null;
    const view = views.find((v) => v.id === viewId);
    return inferViewStageId(view?.filters, pipeline);
  }, [viewId, views, pipeline]);
}
